import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import * as Backend from '@spacefill/shared/src/utils/Backend';
import { getWarehouseLabel } from '@spacefill/shared/src/utils/Warehouse';
import { useUserContext } from '@spacefill/shared/src/utils/contexts/UserContext';
import { Divider } from '@spacefill/uikit/src/components/Divider';
import { EmptyState } from '@spacefill/uikit/src/components/EmptyState';
import { InAppNotification } from '@spacefill/uikit/src/components/InAppNotification';
import { Loader } from '@spacefill/uikit/src/components/Loader';
import { SkeletonRow } from '@spacefill/uikit/src/components/Skeletons/SkeletonRow';
import { toast } from '@spacefill/uikit/src/components/Toast/createToast';

import emptyNotificationSVG from '../../assets/empty-states/empty-state-notification.svg';
import { templates } from './InAppNotificationsTemplates';

const PER_PAGE = 10;

function NotificationSkeleton() {
    return (
        <div className='py-3'>
            <SkeletonRow
                rows={[['w-1/3', 'w-1/3'], ['w-1/3', 'w-2/5', 'w-20'], 'w-full', 'w-1/2']}
                height='h-4'
            />
        </div>
    );
}

export function InAppNotificationsList({ unreadCount }) {
    const { user } = useUserContext();
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [showInitialLoading, setShowInitialLoading] = useState(true);

    const { data, isFetchingNextPage, hasNextPage, fetchNextPage, isFetching, isLoading } = useInfiniteQuery({
        queryKey: ['in-app-notifications', PER_PAGE],
        queryFn: ({ pageParam = 0 }) =>
            Backend.call('in-app-notifications', { limit: PER_PAGE, offset: pageParam * PER_PAGE }),
        getNextPageParam: (lastPage) => {
            return lastPage.nextPage ?? undefined; // undefined is important to have `hasNextPage` false
        },
        refetchOnWindowFocus: true,
    });

    useEffect(() => {
        if (!isLoading && !isFetching && data?.pages?.[0]) {
            setShowInitialLoading(false);
        }
    }, [isLoading, isFetching, data]);

    const { mutate: readMutate } = useMutation({
        mutationFn: ({ notificationId }) => Backend.call(`in-app-notifications/${notificationId}/read`, {}, 'POST'),
        onSuccess() {
            queryClient.invalidateQueries({
                queryKey: ['in-app-notifications'],
            });
            queryClient.invalidateQueries({
                queryKey: ['in-app-notifications/count/unread'],
            });
        },
        onError(error) {
            console.error('error during read mutation: ', error);
            toast.error(t('Internal error!'));
        },
    });

    const observer = useRef();
    const lastProjectElementRef = useCallback(
        (node) => {
            if (isFetchingNextPage) return;
            if (observer.current) observer.current.disconnect();
            observer.current = new window.IntersectionObserver((entries) => {
                if (entries[0].isIntersecting && hasNextPage) {
                    fetchNextPage();
                }
            });
            if (node) observer.current.observe(node);
        },
        [isFetchingNextPage, fetchNextPage, hasNextPage]
    );

    const totalCount = data?.pages?.[0]?.count;
    const loadedCount = data?.pages?.reduce((acc, page) => acc + (page.data?.length || 0), 0) || 0;
    const remainingCount = totalCount - loadedCount;
    const skeletonCount = showInitialLoading
        ? Math.min(unreadCount || 4, 4)
        : isFetching
          ? Math.min(remainingCount || 4, 4)
          : 0;

    return (
        <div>
            {!showInitialLoading && totalCount === 0 && (
                <EmptyState
                    picture={{
                        src: emptyNotificationSVG,
                        width: 80,
                        height: 121,
                    }}
                    paragraph={t('No notification yet. New comments and alerts will be displayed here.')}
                />
            )}
            {(data?.pages || []).map((notifications, pageIndex) => {
                return (
                    <Fragment key={pageIndex}>
                        {(notifications.data || []).map((notification, notificationIndex) => {
                            if (!(notification.templateId in templates)) {
                                return null;
                            }
                            const notificationTemplate = templates[notification.templateId];
                            const isLastPage = pageIndex === data?.pages?.length - 1;
                            const isLastNotification = notificationIndex === notifications.data.length - 1;
                            const isLastElement = isLastPage && isLastNotification && !hasNextPage;

                            return (
                                <Link
                                    key={`InAppNotification-${notification.id}`}
                                    to={notificationTemplate.url(notification)}
                                    onClick={() => readMutate({ notificationId: notification.id })}
                                >
                                    <InAppNotification
                                        key={notification.id}
                                        title={notificationTemplate.title(notification)}
                                        titleIcon={notificationTemplate.icon(notification)}
                                        user={notification.templateParams.createdBy}
                                        createdFromRuleId={notification.templateParams.createdFromRuleId}
                                        receivedAt={new Date(notification.createdAt)}
                                        warehouseName={getWarehouseLabel(user, notification.templateParams.warehouse)}
                                        unread={!notification.readAt}
                                    >
                                        {notificationTemplate.content(notification)}
                                    </InAppNotification>
                                    {isLastElement ? <div className='h-4' /> : <Divider />}
                                </Link>
                            );
                        })}
                    </Fragment>
                );
            })}
            {(showInitialLoading || isFetching) && skeletonCount > 0 && (
                <>
                    {Array.from({ length: skeletonCount }).map((_, index) => (
                        <Fragment key={index}>
                            <NotificationSkeleton />
                            {index < skeletonCount - 1 && <Divider />}
                        </Fragment>
                    ))}
                </>
            )}
            {hasNextPage && (
                <div ref={lastProjectElementRef}>
                    <Loader />
                </div>
            )}
        </div>
    );
}
